"use client"

import { useState, useEffect, useMemo } from "react"
import Image from "next/image"

interface HeroProps {
  initialImages: string[]
}

const Hero = ({ initialImages }: HeroProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [previousImageIndex, setPreviousImageIndex] = useState(0)
  const [shouldAnimate, setShouldAnimate] = useState(false)

  useEffect(() => {
    if (initialImages.length === 0) return

    // Change image every 5 seconds
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % initialImages.length
        setPreviousImageIndex(prevIndex)
        return nextIndex
      })
      setShouldAnimate(true)
    }, 5000)

    return () => clearInterval(interval)
  }, [initialImages])

  useEffect(() => {
    // Reset animation after it completes
    if (shouldAnimate) {
      const timer = setTimeout(() => {
        setShouldAnimate(false)
      }, 3500)
      return () => clearTimeout(timer)
    }
  }, [shouldAnimate])

  // Only mount images that are actually needed:
  // - current (always visible)
  // - previous (during crossfade animation)
  // - next (preload so it's ready before the transition fires)
  const activeIndices = useMemo(() => {
    if (initialImages.length === 0) return new Set<number>()

    const indices = new Set<number>()
    indices.add(currentImageIndex)

    if (shouldAnimate) {
      indices.add(previousImageIndex)
    }

    // Preload the upcoming image so it's cached before the interval fires
    const nextIndex = (currentImageIndex + 1) % initialImages.length
    indices.add(nextIndex)

    return indices
  }, [
    currentImageIndex,
    previousImageIndex,
    shouldAnimate,
    initialImages.length,
  ])

  return (
    <div
      id="hero"
      className="min-h-[calc(100vh-4rem)] w-full relative z-0 overflow-hidden bg-ui-bg-subtle"
    >
      {/* Only 2-3 images are mounted at any time instead of all */}
      <div className="absolute inset-0 w-full h-full">
        {initialImages.length > 0 &&
          initialImages.map((image, index) => {
            if (!activeIndices.has(index)) return null

            const isCurrent = index === currentImageIndex
            const isPrevious = index === previousImageIndex && shouldAnimate

            return (
              <Image
                key={`image-${index}`}
                src={image}
                alt="Hero carousel image"
                fill
                className={`object-cover w-full h-full ${
                  isPrevious ? "animate-hero-fade-out z-[1]" : ""
                } ${
                  isCurrent && shouldAnimate ? "animate-hero-fade-in z-[2]" : ""
                } ${isCurrent && !shouldAnimate ? "opacity-100" : "opacity-0"}`}
                // Only the SSR-visible first image gets priority/preload;
                // the rest lazy-load naturally as they enter activeIndices
                priority={index === 0}
                sizes="100vw"
                unoptimized
              />
            )
          })}
      </div>
    </div>
  )
}

export default Hero
